import React, { useEffect, useState } from "react";
import { Table, Form, Button, Spinner, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import useAppVersions from "hooks/appversions/useAppVersions";
import { OLIVEEB_APP_OPTIONS } from "constants";

const STATUS_OPTIONS = [
  { key: "DRAFT", label: "Draft" },
  { key: "ACTIVE", label: "Active" },
  { key: "CURRENT", label: "Current" },
  { key: "EXPIRED", label: "Expired" },
  { key: "DISABLED", label: "Disabled" },
];

const EditAppVersionForm = ({
  onClose,
  editAppVersionId,
  handleEdited,
  loading,
}) => {
  const [version, setVersion] = useState("");
  const [appName, setAppName] = useState("olitask-mobile-ui");
  const [status, setStatus] = useState("DRAFT");
  const [releaseDate, setReleaseDate] = useState("");
  const [description, setDescription] = useState("");
  const [versionCode, setVersionCode] = useState("");
  const [androidStoreUrl, setAndroidStoreUrl] = useState("");
  const [appleStoreUrl, setAppleStoreUrl] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  const [updateTitle, setUpdateTitle] = useState("");
  const [isRecommended, setIsRecommended] = useState(false);
  const [isMinimumSupported, setIsMinimumSupported] = useState(false);
  const [buildNumber, setBuildNumber] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  const { getAppVersionById } = useAppVersions();

  useEffect(() => {
    // Fetch existing app version details using editAppVersionId
    const fetchAppVersionDetails = async () => {
      try {
        const appVersion = await getAppVersionById(editAppVersionId);

        setVersion(appVersion.versionName || "");
        setAppName(appVersion.appName || "olitask-mobile-ui");
        setStatus(appVersion.status || "DRAFT");
        setReleaseDate(
          appVersion.effectiveFrom
            ? appVersion.effectiveFrom.split("T")[0]
            : "",
        );
        setDescription(appVersion.updateMessage || "");
        setVersionCode(
          appVersion.versionCode ? String(appVersion.versionCode) : "",
        );
        setBuildNumber(appVersion.buildNumber)
        setAndroidStoreUrl(appVersion.androidStoreUrl || "");
        setAppleStoreUrl(appVersion.appleStoreUrl || "");
        setForceUpdate(!!appVersion.forceUpdate);
        setIsMinimumSupported(!!appVersion.isMinimumSupported);
        setIsRecommended(!!appVersion.isRecommended);
        setUpdateTitle(appVersion.updateTitle || "Available");
      } catch (err) {
        const msg = err.message || "Failed to fetch app version details";
        setLocalError(msg);
        toast.error(msg);
      }
    };
    if (editAppVersionId) {
      fetchAppVersionDetails();
    }
  }, [editAppVersionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);

    console.log(
      "Edit form submitting with editAppVersionId:",
      editAppVersionId,
    );

    // Validate that we have the required ID
    if (!editAppVersionId) {
      const msg =
        "App version ID is missing. Please try opening the edit form again.";
      setLocalError(msg);
      toast.error(msg);
      return;
    }

    // Validate required fields
    if (!version || !version.trim()) {
      const msg = "Version name is required and cannot be empty.";
      setLocalError(msg);
      toast.error(msg);
      return;
    }

    if (
      !versionCode ||
      isNaN(Number(versionCode)) ||
      Number(versionCode) <= 0
    ) {
      const msg = "Version code must be a valid positive number.";
      setLocalError(msg);
      toast.error(msg);
      return;
    }

    // Additional validation for update message
    if (!description || !description.trim()) {
      const msg = "Update message is required.";
      setLocalError(msg);
      toast.error(msg);
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        appName: appName,
        versionName: version.trim(),
        versionCode: Number(versionCode),
        androidStoreUrl: androidStoreUrl,
        appleStoreUrl: appleStoreUrl,
        forceUpdate: forceUpdate,
        updateTitle: updateTitle,
        updateMessage: description.trim(),
        isMinimumSupported: isMinimumSupported,
        isRecommended: isRecommended,
        buildNumber: Number(buildNumber),
        effectiveFrom: new Date(releaseDate).toISOString(),
      };

      // Note: effectiveFrom/releaseDate is not included in updates
      // as it might be handled separately or not expected by the backend

      await handleEdited(editAppVersionId, payload);
      onClose && onClose();
    } catch (err) {
      const msg = err.message || "Failed to edit app version";
      setLocalError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {localError && (
        <Alert variant="danger" className="mb-3 text-xs">
          {localError}
        </Alert>
      )}

      <div className="mb-3">
        <Form.Label>App</Form.Label>
        <Form.Select
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          disabled={isSubmitting}
          required
        >
          {OLIVEEB_APP_OPTIONS.map((opt) => (
            <option key={opt.label} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
      </div>

      <div className="mb-3">
        <Form.Label>Version Name</Form.Label>
        <Form.Control
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          disabled={isSubmitting}
          placeholder="e.g. 1.2.0"
          required
        />
      </div>

      <div className="mb-3">
        <Form.Label>Status</Form.Label>
        <Form.Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          disabled={isSubmitting}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
      </div>

      <div className="mb-3">
        <Form.Label>Version Code (android)</Form.Label>
        <Form.Control
          value={versionCode}
          onChange={(e) => setVersionCode(e.target.value)}
          disabled={isSubmitting}
          placeholder="e.g. 102"
          required
          isInvalid={!versionCode.trim() && localError}
        />
        <Form.Text className="text-gray small">
          Android versionCode must be higher than previous release.
        </Form.Text>
      </div>

      <div className="mb-3">
        <Form.Label>Build Number (ios)</Form.Label>
        <Form.Control
          value={buildNumber}
          onChange={(e) => setBuildNumber(e.target.value)}
          disabled={isSubmitting}
          placeholder="e.g. 15"
          required
        />
        <Form.Text className="text-gray small">
          iOS build number must be higher than the previous release.
        </Form.Text>
      </div>

      <div className="mb-3">
        <Form.Label>Apple Store URL</Form.Label>
        <Form.Control
          value={appleStoreUrl}
          onChange={(e) => setAppleStoreUrl(e.target.value)}
          disabled={isSubmitting}
          placeholder="https://apps.apple.com/app/id123456789"
          required
        />
      </div>

      <div className="mb-3">
        <Form.Label>Android Store URL</Form.Label>
        <Form.Control
          value={androidStoreUrl}
          onChange={(e) => setAndroidStoreUrl(e.target.value)}
          disabled={isSubmitting}
          placeholder="https://play.google.com/store/apps/details?id=com.example.app"
          required
        />
      </div>

      <div className="mb-3">
        <Form.Label>Effective From</Form.Label>
        <Form.Control
          type="date"
          value={releaseDate}
          onChange={(e) => setReleaseDate(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="mb-3">
        <Form.Label>Update Title</Form.Label>
        <Form.Control
          value={updateTitle}
          onChange={(e) => setUpdateTitle(e.target.value)}
          disabled={isSubmitting}
          placeholder="e.g. Critical Security Update"
          required
        />
      </div>

      <div className="mb-3">
        <Form.Label>Update Message</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isSubmitting}
          placeholder="Describe what's new in this release..."
          required
        />
      </div>

      <div className="mb-3 d-flex gap-5">
        <Form.Check
          type="checkbox"
          label="Force users to update"
          checked={forceUpdate}
          onChange={(e) => setForceUpdate(e.target.checked)}
          disabled={isSubmitting}
        />

        <Form.Check
          type="checkbox"
          label="Minimum supported version"
          checked={isMinimumSupported}
          onChange={(e) => setIsMinimumSupported(e.target.checked)}
          disabled={isSubmitting}
        />

        <Form.Check
          type="checkbox"
          label="Mark as recommended version"
          checked={isRecommended}
          onChange={(e) => setIsRecommended(e.target.checked)}
          disabled={isSubmitting}
        />
      </div>

      <div className="d-flex justify-content-end gap-2">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSubmitting}
          type="button"
          className="text-sm"
        >
          Cancel
        </Button>

        <Button
          variant="success"
          type="submit"
          disabled={loading || isSubmitting}
          className="text-sm"
        >
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Edit App Version"
          )}
        </Button>
      </div>
    </Form>
  );
};

export default EditAppVersionForm;
